/* Maps real inbody_scans rows onto the design's InbodyResults data shape
   (INBODY_LATEST / INBODY_TRENDS / INBODY_SEGMENTS / INBODY_TREND_X). */

import {
  INBODY_GUIDANCE,
  inbodyStatus,
  type InbodyStatus,
} from "./inbodyExport";

const MONTHS = [
  "jan",
  "feb",
  "mar",
  "apr",
  "may",
  "jun",
  "jul",
  "aug",
  "sep",
  "oct",
  "nov",
  "dec",
];

export type InbodyScanRow = {
  scan_date: string;
  inbody_score: number | null;
  weight: number | null;
  total_body_water: number | null;
  protein: number | null;
  visceral_fat_level: number | null;
  skeletal_muscle_mass: number | null;
  body_fat_mass: number | null;
  bmi: number | null;
  percent_body_fat: number | null;
  weight_normal_min: number | null;
  weight_normal_max: number | null;
  smm_normal_min: number | null;
  smm_normal_max: number | null;
  bfm_normal_min: number | null;
  bfm_normal_max: number | null;
  // Scanner norms behind the good / bad status on the tiles. Optional: older
  // rows and hand-added scans may not carry them.
  tbw_normal_min?: number | null;
  tbw_normal_max?: number | null;
  protein_normal_min?: number | null;
  protein_normal_max?: number | null;
  pbf_normal_min?: number | null;
  pbf_normal_max?: number | null;
  bmi_normal_min?: number | null;
  bmi_normal_max?: number | null;
  gender?: string | null;
  segmental_lean_left_arm: number | null;
  segmental_lean_left_arm_percent: number | null;
  segmental_lean_right_arm: number | null;
  segmental_lean_right_arm_percent: number | null;
  segmental_lean_trunk: number | null;
  segmental_lean_trunk_percent: number | null;
  segmental_lean_left_leg: number | null;
  segmental_lean_left_leg_percent: number | null;
  segmental_lean_right_leg: number | null;
  segmental_lean_right_leg_percent: number | null;
  segmental_fat_left_arm: number | null;
  segmental_fat_left_arm_percent: number | null;
  segmental_fat_right_arm: number | null;
  segmental_fat_right_arm_percent: number | null;
  segmental_fat_trunk: number | null;
  segmental_fat_trunk_percent: number | null;
  segmental_fat_left_leg: number | null;
  segmental_fat_left_leg_percent: number | null;
  segmental_fat_right_leg: number | null;
  segmental_fat_right_leg_percent: number | null;
};

type Tone = "good" | "watch" | "flat";
/** Is the reading itself healthy (not just up or down), plus what it means. */
type Guidance = { status: InbodyStatus | null; note: string };
type Cell = { kg: string; pct: number };
export type InbodyData = {
  latest: {
    date: string;
    score: number | string;
    prevDelta: number;
    comp: ({
      label: string;
      val: string;
      unit: string;
      chg: string;
      tone: Tone;
    } & Guidance)[];
    ranges: {
      label: string;
      val: string;
      chg: string;
      tone: Tone;
      pos: number;
    }[];
    bmi: { val: string; chg: string; tone: Tone } & Partial<Guidance>;
    fatPct: { val: string; chg: string; tone: Tone } & Partial<Guidance>;
  };
  trends: Record<string, { unit: string; pts: number[] }>;
  trendX: string[];
  /** Full inbody-score history (raw date + label + value) for range filtering. */
  scoreSeries: { date: string; label: string; v: number }[];
  segments: { lean: Record<string, Cell>; fat: Record<string, Cell> };
  /** Every loaded scan, oldest first — lets the UI re-average per time range. */
  rows: InbodyScanRow[];
};

/** A range-filtered view of the reads: averaged when the range spans days. */
export type InbodyView = {
  latest: InbodyData["latest"];
  segments: InbodyData["segments"];
  trends: InbodyData["trends"];
  trendX: string[];
  /** How many scans the numbers are drawn from (1 = a single reading). */
  scanCount: number;
  /** True when the numbers are a period average rather than one reading. */
  isAverage: boolean;
};

// scan_date may be a date-only string ("2026-04-12") or a full ISO timestamp
// ("2026-04-12T17:16:00+00:00"). Parse both: date-only is read at local
// midnight (avoids a TZ off-by-one), timestamps are parsed as-is.
const parseScanDate = (s: string): Date => {
  if (!s) return new Date(NaN);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return new Date(s + "T00:00:00");
  return new Date(s);
};
const fmtDate = (s: string) => {
  const d = parseScanDate(s);
  if (isNaN(d.getTime())) return s;
  // Keep it short — day + month, no year.
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
};
const shortDate = (s: string) => {
  const d = parseScanDate(s);
  if (isNaN(d.getTime())) return s;
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
};
const n1 = (v: number | null | undefined) =>
  v == null ? "–" : (Math.round(v * 10) / 10).toString();
const clamp01 = (x: number) => Math.max(0, Math.min(1, x));

// Signed change string, e.g. "+0.2" / "-0.8" (optionally with a suffix).
const chgStr = (cur: number | null, prev: number | null, suffix = "") => {
  if (cur == null || prev == null) return "0" + suffix;
  const d = Math.round((cur - prev) * 10) / 10;
  return (d >= 0 ? "+" : "") + d + suffix;
};
// Tone where lower is better (fat, weight, visceral, bmi).
const toneDown = (cur: number | null, prev: number | null): Tone => {
  if (cur == null || prev == null || cur === prev) return "flat";
  return cur < prev ? "good" : "watch";
};
// Tone where higher is better (muscle, water, protein).
const toneUp = (cur: number | null, prev: number | null): Tone => {
  if (cur == null || prev == null || cur === prev) return "flat";
  return cur > prev ? "good" : "watch";
};

// Position 0..1 across the under | normal | over bar.
const rangePos = (
  val: number | null,
  min: number | null,
  max: number | null,
): number => {
  if (val == null || min == null || max == null || max <= min) return 0.5;
  if (val < min) return clamp01(0.16 * (val / min));
  if (val > max) return clamp01(0.84 + 0.16 * Math.min(1, (val - max) / max));
  return 0.16 + ((val - min) / (max - min)) * 0.68;
};

const guide = (col: string, row: InbodyScanRow): Guidance => ({
  status: inbodyStatus(col, row as unknown as { scan_date: string }),
  note: INBODY_GUIDANCE[col]?.note ?? "",
});

const cell = (kg: number | null, pct: number | null): Cell => ({
  kg: n1(kg),
  pct: pct == null ? 100 : Math.round(pct),
});

/** Build the score / composition / muscle-fat block for one reading (or one
 *  period average) against the reading (or period average) before it. */
function buildLatest(
  L: InbodyScanRow,
  P: InbodyScanRow | null,
  dateLabel: string,
): InbodyData["latest"] {
  return {
    date: dateLabel,
    score: L.inbody_score ?? "–",
    prevDelta:
      L.inbody_score != null && P?.inbody_score != null
        ? L.inbody_score - P.inbody_score
        : 0,
    comp: [
      {
        label: "weight",
        val: n1(L.weight),
        unit: "kg",
        chg: chgStr(L.weight, P?.weight ?? null),
        tone: toneDown(L.weight, P?.weight ?? null),
        ...guide("weight", L),
      },
      {
        label: "body water",
        val: n1(L.total_body_water),
        unit: "L",
        chg: chgStr(L.total_body_water, P?.total_body_water ?? null),
        tone: toneUp(L.total_body_water, P?.total_body_water ?? null),
        ...guide("total_body_water", L),
      },
      {
        label: "protein",
        val: n1(L.protein),
        unit: "kg",
        chg: chgStr(L.protein, P?.protein ?? null),
        tone: toneUp(L.protein, P?.protein ?? null),
        ...guide("protein", L),
      },
      {
        label: "visceral fat",
        val:
          L.visceral_fat_level == null
            ? "–"
            : String(Math.round(L.visceral_fat_level)),
        unit: "level",
        chg: chgStr(L.visceral_fat_level, P?.visceral_fat_level ?? null),
        tone: toneDown(L.visceral_fat_level, P?.visceral_fat_level ?? null),
        ...guide("visceral_fat_level", L),
      },
    ],
    ranges: [
      {
        label: "weight",
        val: `${n1(L.weight)} kg`,
        chg: chgStr(L.weight, P?.weight ?? null),
        tone: toneDown(L.weight, P?.weight ?? null),
        pos: rangePos(L.weight, L.weight_normal_min, L.weight_normal_max),
      },
      {
        label: "muscle",
        val: `${n1(L.skeletal_muscle_mass)} kg`,
        chg: chgStr(L.skeletal_muscle_mass, P?.skeletal_muscle_mass ?? null),
        tone: toneUp(L.skeletal_muscle_mass, P?.skeletal_muscle_mass ?? null),
        pos: rangePos(
          L.skeletal_muscle_mass,
          L.smm_normal_min,
          L.smm_normal_max,
        ),
      },
      {
        label: "body fat",
        val: `${n1(L.body_fat_mass)} kg`,
        chg: chgStr(L.body_fat_mass, P?.body_fat_mass ?? null),
        tone: toneDown(L.body_fat_mass, P?.body_fat_mass ?? null),
        pos: rangePos(L.body_fat_mass, L.bfm_normal_min, L.bfm_normal_max),
      },
    ],
    bmi: {
      val: n1(L.bmi),
      chg: chgStr(L.bmi, P?.bmi ?? null),
      tone: toneDown(L.bmi, P?.bmi ?? null),
      ...guide("bmi", L),
    },
    fatPct: {
      val: n1(L.percent_body_fat),
      chg: chgStr(L.percent_body_fat, P?.percent_body_fat ?? null, "%"),
      tone: toneDown(L.percent_body_fat, P?.percent_body_fat ?? null),
      ...guide("percent_body_fat", L),
    },
  };
}

function buildSegments(L: InbodyScanRow): InbodyData["segments"] {
  return {
    lean: {
      "left arm": cell(
        L.segmental_lean_left_arm,
        L.segmental_lean_left_arm_percent,
      ),
      "right arm": cell(
        L.segmental_lean_right_arm,
        L.segmental_lean_right_arm_percent,
      ),
      trunk: cell(L.segmental_lean_trunk, L.segmental_lean_trunk_percent),
      "left leg": cell(
        L.segmental_lean_left_leg,
        L.segmental_lean_left_leg_percent,
      ),
      "right leg": cell(
        L.segmental_lean_right_leg,
        L.segmental_lean_right_leg_percent,
      ),
    },
    fat: {
      "left arm": cell(
        L.segmental_fat_left_arm,
        L.segmental_fat_left_arm_percent,
      ),
      "right arm": cell(
        L.segmental_fat_right_arm,
        L.segmental_fat_right_arm_percent,
      ),
      trunk: cell(L.segmental_fat_trunk, L.segmental_fat_trunk_percent),
      "left leg": cell(
        L.segmental_fat_left_leg,
        L.segmental_fat_left_leg_percent,
      ),
      "right leg": cell(
        L.segmental_fat_right_leg,
        L.segmental_fat_right_leg_percent,
      ),
    },
  };
}

// History — last up to 7 readings. The chart divides by (points - 1), so
// guarantee at least two points (a lone scan renders as a flat line).
function buildTrends(rowsAsc: InbodyScanRow[]): {
  trends: InbodyData["trends"];
  trendX: string[];
} {
  const recent = rowsAsc.slice(-7);
  const hist = recent.length === 1 ? [recent[0], recent[0]] : recent;
  const col = (rows: InbodyScanRow[], key: keyof InbodyScanRow) =>
    rows.map((r) => {
      const v = r[key];
      return v == null ? 0 : Math.round((v as number) * 10) / 10;
    });
  return {
    trends: {
      weight: { unit: "kg", pts: col(hist, "weight") },
      muscle: { unit: "kg", pts: col(hist, "skeletal_muscle_mass") },
      "fat %": { unit: "%", pts: col(hist, "percent_body_fat") },
    },
    trendX: hist.map((r) => shortDate(r.scan_date)),
  };
}

export function buildInbody(scansAsc: InbodyScanRow[]): InbodyData | null {
  if (!scansAsc || scansAsc.length === 0) return null;
  const L = scansAsc[scansAsc.length - 1];
  const P = scansAsc.length > 1 ? scansAsc[scansAsc.length - 2] : null;

  const latest = buildLatest(L, P, fmtDate(L.scan_date));
  const { trends, trendX } = buildTrends(scansAsc);
  const segments = buildSegments(L);

  // Full score history (all loaded scans, not just the last 7) so the UI can
  // filter to day / week / month / 3-6 months / year ranges.
  const scoreSeries = scansAsc
    .filter((r) => r.inbody_score != null)
    .map((r) => ({
      date: r.scan_date,
      label: shortDate(r.scan_date),
      v: r.inbody_score as number,
    }));

  return { latest, trends, trendX, scoreSeries, segments, rows: scansAsc };
}

/* ═══════════════ range-aware averaging ═══════════════ */

export const IB_RANGE_DAYS: Record<string, number> = {
  "1D": 1,
  "7D": 7,
  "30D": 30,
  "90D": 90,
  "180D": 180,
  "365D": 365,
};

// Reference bands come from the scanner's own norms for the member, so they're
// carried over from the most recent scan rather than averaged.
const AVG_SKIP = new Set<keyof InbodyScanRow>([
  "scan_date",
  "weight_normal_min",
  "weight_normal_max",
  "smm_normal_min",
  "smm_normal_max",
  "bfm_normal_min",
  "bfm_normal_max",
  "tbw_normal_min",
  "tbw_normal_max",
  "protein_normal_min",
  "protein_normal_max",
  "pbf_normal_min",
  "pbf_normal_max",
  "bmi_normal_min",
  "bmi_normal_max",
  "gender",
]);

/** Average every measured field across a set of scans. Fields nobody recorded
 *  stay null; the newest scan supplies the date and the reference bands. */
export function averageRows(rows: InbodyScanRow[]): InbodyScanRow | null {
  if (!rows || rows.length === 0) return null;
  if (rows.length === 1) return rows[0];

  const newest = rows[rows.length - 1];
  const out: Record<string, unknown> = { ...newest };
  const keys = new Set<string>();
  rows.forEach((r) => Object.keys(r).forEach((k) => keys.add(k)));

  keys.forEach((k) => {
    if (AVG_SKIP.has(k as keyof InbodyScanRow)) return;
    const vals = rows
      .map((r) => (r as unknown as Record<string, unknown>)[k])
      .filter((v): v is number => typeof v === "number" && !isNaN(v));
    if (vals.length === 0) {
      out[k] = null;
      return;
    }
    const mean = vals.reduce((a, b) => a + b, 0) / vals.length;
    // The score reads as a whole number; everything else keeps one decimal.
    out[k] = k === "inbody_score" ? Math.round(mean) : mean;
  });

  return out as unknown as InbodyScanRow;
}

const startOfDayMs = (d: Date) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

const inWindow = (rows: InbodyScanRow[], from: number, to: number) =>
  rows.filter((r) => {
    const t = parseScanDate(r.scan_date).getTime();
    return !isNaN(t) && t >= from && t <= to;
  });

// Group the window's scans so a long range doesn't plot one point per scan:
// a week or month of scans reads per day, half a year per week, a year per month.
const bucketOf = (d: Date, size: "day" | "week" | "month") => {
  if (size === "month") return `${d.getFullYear()}-${d.getMonth()}`;
  if (size === "week") {
    const wk = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    // ISO-ish week start (monday).
    const dow = wk.getDay();
    wk.setDate(wk.getDate() - (dow === 0 ? 6 : dow - 1));
    return `w${wk.getTime()}`;
  }
  return `d${startOfDayMs(d)}`;
};

const bucketSizeFor = (days: number): "day" | "week" | "month" =>
  days > 180 ? "month" : days > 30 ? "week" : "day";

/** Average the window's scans into chart points — one per day / week / month. */
function bucketRows(rows: InbodyScanRow[], days: number): InbodyScanRow[] {
  const size = bucketSizeFor(days);
  const groups = new Map<string, InbodyScanRow[]>();
  rows.forEach((r) => {
    const d = parseScanDate(r.scan_date);
    if (isNaN(d.getTime())) return;
    const key = bucketOf(d, size);
    const g = groups.get(key);
    if (g) g.push(r);
    else groups.set(key, [r]);
  });
  return Array.from(groups.values())
    .map((g) => averageRows(g))
    .filter((r): r is InbodyScanRow => r != null)
    .sort(
      (a, b) =>
        parseScanDate(a.scan_date).getTime() -
        parseScanDate(b.scan_date).getTime(),
    );
}

/**
 * Build the reads for the selected time range.
 *
 * "1D" (and a tapped point on the score chart) shows that single reading.
 * Every longer range averages every scan in the window — score, body
 * composition, muscle-fat, segmental and the history chart alike — and
 * compares it against the average of the window before it.
 *
 * @param rowsAsc   all scans, oldest first
 * @param rangeKey  one of IB_RANGE_DAYS ("1D" … "365D")
 * @param selDate   scan_date of a tapped score point — pins to that reading
 * @param now       reference "today" (injectable for tests)
 */
export function buildInbodyView(
  rowsAsc: InbodyScanRow[],
  rangeKey: string,
  selDate?: string | null,
  now: Date = new Date(),
): InbodyView | null {
  if (!rowsAsc || rowsAsc.length === 0) return null;

  const single = (row: InbodyScanRow): InbodyView => {
    const idx = rowsAsc.indexOf(row);
    const prev = idx > 0 ? rowsAsc[idx - 1] : null;
    const { trends, trendX } = buildTrends(
      idx >= 0 ? rowsAsc.slice(0, idx + 1) : rowsAsc,
    );
    return {
      latest: buildLatest(row, prev, fmtDate(row.scan_date)),
      segments: buildSegments(row),
      trends,
      trendX,
      scanCount: 1,
      isAverage: false,
    };
  };

  // A tapped point on the score chart pins every section to that reading.
  if (selDate) {
    const hit = rowsAsc.find((r) => r.scan_date === selDate);
    if (hit) return single(hit);
  }

  const days = IB_RANGE_DAYS[rangeKey] ?? 1;
  const newest = rowsAsc[rowsAsc.length - 1];
  if (days <= 1) return single(newest);

  const end = startOfDayMs(now) + 86399999;
  const start = startOfDayMs(new Date(now.getTime() - (days - 1) * 86400000));
  const current = inWindow(rowsAsc, start, end);
  // Nothing in range — fall back to the newest reading rather than a blank card.
  if (current.length === 0) return single(newest);

  const prevWindow = inWindow(rowsAsc, start - days * 86400000, start - 1);
  const prevAvg =
    averageRows(prevWindow) ??
    // No scans in the window before — compare against the last one before it.
    (() => {
      const earlier = rowsAsc.filter(
        (r) => parseScanDate(r.scan_date).getTime() < start,
      );
      return earlier.length ? earlier[earlier.length - 1] : null;
    })();

  const curAvg = averageRows(current) as InbodyScanRow;
  const label = `${shortDate(current[0].scan_date)} – ${shortDate(current[current.length - 1].scan_date)}`;
  const { trends, trendX } = buildTrends(bucketRows(current, days));

  return {
    latest: buildLatest(curAvg, prevAvg, label),
    segments: buildSegments(curAvg),
    trends,
    trendX,
    scanCount: current.length,
    isAverage: current.length > 1,
  };
}
